<template>
  <div class="export-btns-container">
    <div class="export-btns" v-if="data && data.length">
      <div class="btn add-btn export" @click="exportToExcel">
        <i class="fa-solid fa-file-excel"></i>
      </div>
      <div
        v-if="hidePdf ? false : true"
        class="btn add-btn export"
        @click="exportToPDF"
      >
        <i class="fa-solid fa-file-pdf"></i>
      </div>
    </div>

    <input
      v-if="hideSearch ? false : true"
      type="text"
      class="form-control"
      @input="handleChange"
      @focus="isFocused = true"
      @blur="isFocused = employee_id !== ''"
      placeholder="Search Table Data"
    />
  </div>
</template>

<script>
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

import jsPDF from "jspdf";
import "jspdf-autotable";
import { emits } from "@/staticData";
import { searchData } from "@/utils";

export default {
  props: [
    "fileName",
    "data",
    "columns",
    "parent",
    "hidePdf",
    "hideSearch",
    "payrollData",
    "secondaryColumns",
    "secondaryData",
    "tertiaryColumns",
    "tertiaryData",
  ],
  emits: ["search-input-change"],
  data() {
    return {
      isFocusedOn: false,
      text: "",
    };
  },
  methods: {
    exportToExcel() {
      // Create a new workbook and append the worksheet
      const workbook = XLSX.utils.book_new();

      // Create a worksheet from the items data
      const updatedData = [];
      this.data.map((el, index) => {
        updatedData.push({});
        Object.entries(el).forEach(([key, value]) => {
          this.columns.map((item) => {
            if (key === "id" || key === "key") {
              updatedData[index]["ID"] = index + 1;
            } else {
              if (key == item.dataIndex) {
                updatedData[index][item.title] = value;
              }
            }
          });
        });
      });
      const worksheet = XLSX.utils.json_to_sheet(updatedData);
      if (this.payrollData) {
        const numericHeaders = [
          "Salary Per Month",
          "Basic Per Month",
          "Actual Salary As Per Attendance",
          "Arrears",
          "Advances",
          "Incentives",
          "Gross Salary",
          "EOBI",
          "Penalty On NCNS",
          "Provident Fund",
          "Other Deductions",
          "Custom Additions",
          "Total Deductions",
          "Custom Deductions",
          "Net Salary",
          "Income Tax",
        ];
        const commentMapping = {
          "Arrears": "arrears_reason",
          "Advances": "advances_reason",
          "Incentives": "incentives_reason",
          "Other Deductions": "other_deductions_reason",
          "Penalty On NCNS": "ncns_penality_reason",
          "Custom Additions": "custom_addition_reason",
          "Custom Deductions": "custom_deduction_reason",
        };

        Object.keys(worksheet).forEach((cell) => {
          if (cell[0] === "!") return; // skip metadata

          const columnLetter = cell.replace(/[0-9]/g, "");
          const headerCell = worksheet[columnLetter + "1"];
          if (!headerCell) return;

          const headerTitle = headerCell.v;

          if (numericHeaders.includes(headerTitle) && worksheet[cell].t === "n") {
            worksheet[cell].z = "#,##0";
          }

          const reasonKey = commentMapping[headerTitle];
          if (reasonKey) {
            this.data.forEach((row, rowIndex) => {
              const excelRow = rowIndex + 2;
              const cellRef = `${columnLetter}${excelRow}`;
              const reasonText = row[reasonKey];

              if (reasonText) {
                if (!worksheet[cellRef]) worksheet[cellRef] = { t: "s", v: "" };
                worksheet[cellRef].c = [
                  {
                    a: "System",
                    t: reasonText,
                  },
                ];
                worksheet[cellRef].c["hidden"] = true; // Keep comment hidden (show on hover)
              }
            });
          }
        });


        XLSX.utils.book_append_sheet(workbook, worksheet, "Payroll");

        if (this.secondaryData?.length > 0 && this.secondaryColumns?.length > 0) {
          const updatedSecData = [];
          this.secondaryData.map((el, index) => {
            updatedSecData.push({});
            Object.entries(el).forEach(([key, value]) => {
              this.secondaryColumns.map((item) => {
                if (key === "id" || key === "key") {
                  updatedSecData[index]["ID"] = index + 1;
                } else {
                  if (key == item.dataIndex) {
                    updatedSecData[index][item.title] = value;
                  }
                }
              });
            });
          });
          const secWorksheet = XLSX.utils.json_to_sheet(updatedSecData);
          XLSX.utils.book_append_sheet(workbook, secWorksheet, "Attendance");
        }

        if(this.tertiaryData?.length > 0 && this.tertiaryColumns?.length > 0) {
          const updatedTerData = [];
          this.tertiaryData.map((el, index) => {
            updatedTerData.push({});
            Object.entries(el).forEach(([key, value]) => {
              this.tertiaryColumns.map((item) => {
                if (key === "id" || key === "key") {
                  updatedTerData[index]["ID"] = index + 1;
                } else {
                  if (key == item.dataIndex) {
                    updatedTerData[index][item.title] = value;
                  }
                }
              });
            });
          });
          const terWorksheet = XLSX.utils.json_to_sheet(updatedTerData);
          XLSX.utils.book_append_sheet(workbook, terWorksheet, "Leaves");
        }

      } else {
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      }

      // Convert the workbook to a binary string
      const workbookOut = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "binary",
        cellStyles: true, 
      });

      // Convert the binary string to a Blob and save it
      const blob = new Blob([this.s2ab(workbookOut)], {
        type: "application/octet-stream",
      });

      saveAs(blob, `hcm-${this.fileName}.xlsx`);
    },
    s2ab(s) {
      const buf = new ArrayBuffer(s.length);
      const view = new Uint8Array(buf);
      for (let i = 0; i < s.length; i++) {
        view[i] = s.charCodeAt(i) & 0xff;
      }
      return buf;
    },

    exportToPDF() {
      const doc = new jsPDF({
        orientation: "landscape",
      });
      const headers = [];
      this.columns.forEach((el) => {
        if (el.key !== "action") {
          headers.push(el.title);
        }
      });

      console.log("exportToPDF", this.columns, headers);

      // const headers = this.columns.filter().map((field) => field.title);
      const data = this.data.map((item, index) =>
        this.columns.map((field) => {
          if (field.dataIndex == "id" || field.dataIndex == "key") {
            return index + 1;
          } else {
            return item[field.dataIndex];
          }
        })
      );

      doc.autoTable({
        head: [headers],
        body: data,
        columnStyles: {
          0: { cellWidth: "auto" }, // Set the width for the 'name' column
        },
      });

      doc.save(`hcm-${this.fileName}.pdf`);
    },

    handleChange(ev) {
      const value = ev.target.value;
      console.log("parentFilteredData", this.parent);
      if (this.parent?.filteredData && this.parent?.data) {
        this.parent.filteredData = searchData(value, this.parent.data);
      }
    },
    // WIP Dynamic filtered Data list
    // handleChange(ev) {
    //   const value = ev.target.value;

    //   // Find the dynamic key in the parent that matches the passed data
    //   const parentKey = Object.keys(this.parent).find(
    //     (key) => this.parent[key] === this.data
    //   );

    //   // const data = this.data;
    //   console.log("p key", parentKey);
    //   console.log("p obj", this.parent);
    //   console.log("p data", this.data);
    //   // console.log('p data local', data)

    //   if (parentKey) {
    //     const data = this.parent.data;
    //     if (value.trim() === "") {
    //       // Reset to the original data when the input is cleared
    //       console.log("p obj data", data);
    //       this.parent[parentKey] = [...data]; // Ensure `data` in the parent holds the original list
    //     } else {
    //       // Apply filtering
    //       this.parent[parentKey] = searchData(value, this.data);
    //     }
    //   } else {
    //     console.warn(
    //       "The parent does not have a property matching the passed 'data' prop."
    //     );
    //   }
    // },
  },
};
</script>

<style scoped></style>
